import { useCallback, useEffect, useRef, useState } from 'react';
import isHotkey from 'is-hotkey';
import { Editable, useSlate } from 'slate-react';
import { Editor, Node, Text } from 'slate';

import { createPortal } from 'react-dom';
import { Overlay } from '../InteractiveComponents/Overlay';
import Leaf from './Leaf';
import Element from './Element';
import { get } from 'lodash';
import useSelectedElement from '../hooks/useSelectedElement';
import useEmailEditor from '@hooks/useEmailEditor';
import { withHistory } from 'slate-history';
import useMark from '@hooks/useMark';
import EditLink from '@Components/EditLink';
import useLink from '@hooks/useLink';
import i18next from 'i18next';

const HOTKEYS = {
    'mod+b': 'bold',
    'mod+i': 'italic',
    'mod+u': 'underline',
    'mod+shift+s': 'strikethrough',
    'mod+shift++': 'increase',
    'mod+shift+-': 'decrease',
    'mod+k': 'link',
    'mod+s': 'save',
    // 'mod+`': 'code',
};

const srcDoc = `<!DOCTYPE html>
<html lang="en">
<head>
    <style>
        ::-webkit-scrollbar {
            display: none;
        }

        body {
            margin: 0;
        }

        .email-section::before {
            content: '';
            position: absolute;
            top: 0;
            left: -60px;
            width: 60px;
            height: 100%;
        }

        .email-section::after {
            content: '';
            position: absolute;
            top: 0;
            right: -60px;
            width: 60px;
            height: 100%;
        }

    </style>
</head>
<body></body>
</html>`;

const bodyPath = [0, 0];

const Iframe = ({ children, ...props }) => {
    const { setSelectedElement } = useSelectedElement();
    const [loaded, setLoaded] = useState(false);
    const { iframeRef } = useEmailEditor();
    const editor = useSlate() as Editor & ReturnType<typeof withHistory>;

    const resizeObserverRef = useRef(null);
    const [iframeHeight, setIframeHeight] = useState(300);

    const emailBackgroundColor = get(
        Node.get(editor, bodyPath),
        'attributes.style.backgroundColor',
        'none',
    );

    const lang = editor.children[0].theme.lang;
    const dir = i18next.dir(lang);

    useEffect(() => {
        if (iframeRef.current && loaded) {
            const handleResize = () => {
                requestAnimationFrame(() => {
                    setIframeHeight(
                        iframeRef.current.contentDocument.body.scrollHeight,
                    );
                });
            };

            resizeObserverRef.current = new ResizeObserver(handleResize);
            resizeObserverRef.current.observe(
                iframeRef.current.contentDocument.body,
            );

            iframeRef.current.contentDocument.documentElement.lang = lang;
            iframeRef.current.contentDocument.documentElement.dir = dir;

            return () => {
                resizeObserverRef.current.disconnect();
            };
        }
    }, [loaded, iframeRef, lang, dir]);

    return (
        <>
            <div
                id="iframe-container"
                style={{
                    position: 'relative',
                    height: '100%',
                    backgroundColor: emailBackgroundColor,
                }}
                onMouseDown={() => setSelectedElement(null)}
            >
                <iframe
                    srcDoc={srcDoc}
                    width="100%"
                    // height={
                    //     iframeRef.current?.contentWindow?.document.body
                    //         .scrollHeight || 300
                    // }
                    height={iframeHeight}
                    style={{
                        border: 'none',
                    }}
                    {...props}
                    onLoad={() => setLoaded(true)}
                    ref={iframeRef}
                >
                    {loaded &&
                        createPortal(
                            children,
                            iframeRef.current.contentDocument.body,
                        )}
                </iframe>
            </div>
        </>
    );
};

const Canvas = () => {
    const { setLinkOpen, saveEmail } = useEmailEditor();
    const renderElement = useCallback(props => <Element {...props} />, []);

    const renderLeaf = useCallback(props => <Leaf {...props} />, []);
    const editor = useSlate();

    useEffect(() => {
        const handleSave = (event: KeyboardEvent) => {
            if (event.ctrlKey && event.key === 's') {
                event.preventDefault();
                event.stopPropagation();
                saveEmail();
            }
        };

        document.addEventListener('keydown', handleSave);

        return () => {
            document.removeEventListener('keydown', handleSave);
        };
    }, []);

    const decorate = useCallback(([node, path]) => {
        const ranges = [];

        if (!node.text) {
            return ranges;
        }

        const text = node.text;
        // Pattern to match content within double curly brackets (including the brackets)
        const pattern = /(\{\{)(.*?)(\}\})/g;

        let match;
        while ((match = pattern.exec(text)) !== null) {
            // Start position (opening brackets)
            const startOffset = match.index;
            // Content position (between brackets)
            const contentOffset = match.index + 2;
            // End position (closing brackets)
            const endOffset = match.index + match[0].length - 2;

            // Range for opening brackets (to hide them)
            ranges.push({
                anchor: { path, offset: startOffset },
                focus: { path, offset: contentOffset },
                mergedTagIndicator: true,
            });

            // Range for the content (to style it)
            ranges.push({
                anchor: { path, offset: contentOffset },
                focus: { path, offset: endOffset },
                mergedTag: true,
            });

            // Range for closing brackets (to hide them)
            ranges.push({
                anchor: { path, offset: endOffset },
                focus: { path, offset: endOffset + 2 },
                mergedTagIndicator: true,
            });
        }

        return ranges;
    }, []);

    return (
        <>
            <EditLink />
            <Iframe>
                <Editable
                    // disableDefaultStyles
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    spellCheck
                    id="editor"
                    decorate={decorate}
                    // autoFocus
                    // onDragStart={() => true}
                    // onDragOver={() => true}
                    style={{ outline: 'none' }}
                    onKeyDown={event => {
                        for (const hotkey in HOTKEYS) {
                            if (isHotkey(hotkey, event as any)) {
                                event.preventDefault();
                                const mark = HOTKEYS[hotkey];

                                // if (['increase', 'decrease'].includes(mark))
                                //     changeFontSize(mark);
                                if (mark === 'save') {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    saveEmail();
                                }
                                if (mark === 'link') setLinkOpen(true);
                                else toggleMark(editor, mark);
                            }
                        }
                    }}
                />
                <Overlay />
            </Iframe>
        </>
    );
};

// const changeFontSize = action => {
//     const { getMark, setMark, getFallbackValue } = useMark();
//     const size = parseInt(
//         getMark('fontSize') || getFallbackValue('font-size') || '16',
//     );
//     console.log({ size });

//     if (action === 'increase') setMark('fontSize', size + 1 + 'px');
//     else if ('decrease') setMark('fontSize', size - 1 + 'px');
// };

const toggleMark = (editor, format) => {
    const isActive = isMarkActive(editor, format);

    if (isActive) {
        Editor.removeMark(editor, format);
    } else {
        Editor.addMark(editor, format, true);
    }
};

const isMarkActive = (editor, format) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
};

export default Canvas;
